// #6829 — the rule that decides whether a log entry the sync-conflict resolver merged was
// damaged by the #6825 tie-break, and what a repair writes back. Pure: the command-line
// half (`tools/src/repair-resolver-merges-6829.ts`) reads the revisions and the nightly exports
// and hands them in. It lives in `shared/` because the app runs the close-only rule too (#6847),
// against the copies its own conflict archive kept, with its own id in the stamp
// (`RepairStampOptions`).
//
// WHAT THE BUG DID. The resolver (Phase C, #4314) merged a log entry's conflicting copies
// and settled every tied field by whichever copy was written last. An empty `Scheduled`
// placeholder saved after the user completed the entry on another device therefore won
// `state`, and the entry reopened. Real case: user Zn68Plam's weigh-in of 12 Oct, 104 kg
// logged Done on the iPhone, merged on 21/22 Oct into `Scheduled` with 104 kg kept. #6825
// fixed the rule: the copy that speaks more for the user (`logEntryCopyRank`) now wins.
//
// HOW A RESOLVER MERGE IS RECOGNISED. The resolver closes each losing branch with a
// tombstone marked `db__conflictClose: true`, which nothing else writes, and stamps the
// merged winner and that tombstone with the same `db__trans`. So a candidate is a log
// entry whose current winner carries exactly the stamp of a resolver tombstone beside it,
// and has a parent: a merge always writes on top of the copy CouchDB showed, so a
// generation-1 winner was never rewritten (the run only closed branches). A winner with
// any other stamp was written after the merge (by the user, the app, or an earlier repair)
// and is left alone. Two devices closing the same revision count as one close; closes of
// different revisions by separate runs are left to a person, because their order matters.
// The stamps alone do not prove the run wrote the winner: a close-only run on the device that
// saved the winner gives its tombstone the winner's own stamp. So the winner must also have been
// written with the run: by the time an app revision id starts with, or, for a hash id, by
// holding every field of the input copy it would be restored from (a merge keeps every field).
//
// WHAT A REPAIR WRITES, in order of preference:
// 1. a copy from a nightly export of one of the revisions the merge took in (the winner's
//    parent, or a revision the run closed) that outranks the current winner, merged back
//    with the fixed rule's preference (`RestoreFromExport`). That restores logged sets and
//    eaten foods, which the state alone cannot show. An older ancestor is never used: it
//    may predate an edit the user made on purpose. (Found on the first prod read, 2026-10-24.)
// 2. otherwise, for a weigh-in or weekly check-up whose content shows it was completed
//    but which says `Scheduled`: `state` back to Done, nothing else changed
//    (`StateToDone`), the repair #6822's weigh-ins got by hand on 2026-10-24.
// 3. a workout, meal or day marker that may be damaged but cannot be repaired from what
//    survives is only reported (`ReportOnly`). The server compacted the losing copies, so
//    a planned workout's sets or a planned meal's foods cannot be told from the user's.
//
// THE STAMP. A repair is stamped with the restored copy's own write time (or the merge's,
// for a state-only repair) and the device half `server`, never "now". A later merge
// settles equal-rank ties by the newer stamp, so "now" would beat an edit the user made
// on an offline device after the merge. `server` is never any device's own id.

import { mergeConflictingDocsPreferring } from '../deep-helpers';
import { getTransactionTimeMs } from './pouch';
import { logEntryCopyRank, logEntryHoldsUserData } from './log-entry-user-data';
import { hasMeasuredBodyFat } from './log-weigh-in';
import { isLogRecordEntry } from './user-log';

/** the device half of every stamp this repair writes */
export const REPAIR_STAMP_DEVICE = 'server';

/**
 * Who a repair writes as, and how an earlier repair is recognised. Two values, not one: the
 * server tool is never any device's own id, so one word serves both. On a device (#6847) the
 * repair writes with that device's own id, as every save there does, and that id cannot mark a
 * repair: the winner the device saved itself carries it too. So the device passes `null` and
 * relies on its archive index remembering which rows it has judged.
 */
export interface RepairStampOptions {
  /** the device half every written stamp carries */
  stampDevice: string;
  /** a winner whose stamp has this device half was written by a repair; `null` skips the check */
  repairedMarker: string | null;
}

/** what the server tool writes as: `server`, which is also how its repairs are recognised */
export const SERVER_REPAIR_STAMP: RepairStampOptions = { stampDevice: REPAIR_STAMP_DEVICE, repairedMarker: REPAIR_STAMP_DEVICE };

export enum RepairOutcome {
  /** an outranking copy from a nightly export was merged back over the resolver's result */
  RestoreFromExport = 'restore-from-export',
  /** #6836: the exported copy of a revision the resolver closed was written back whole */
  RestoreWhole = 'restore-whole',
  /** a completed weigh-in or check-up had its state set back to Done, nothing else changed */
  StateToDone = 'state-to-done',
  /** possibly damaged, but what survives cannot repair it; listed for a person */
  ReportOnly = 'report-only',
  /** the winner was written by a repair already (device half `server`) */
  AlreadyRepaired = 'already-repaired',
  /** the winner is not the resolver's merge: something wrote on top of it since */
  ChangedSince = 'changed-since',
  /** a resolver merge, but it lost nothing the fixed rule would have kept */
  NotDamaged = 'not-damaged',
  /** #6836: a closed copy may have held user data, but no export holds that exact revision */
  Unrecoverable = 'unrecoverable',
  /** no resolver tombstone, or no live copy: not this bug's shape */
  NoResolverClose = 'no-resolver-close',
  /** more than one resolver tombstone: the fold order matters, so a person looks */
  ManualLook = 'manual-look',
  /** a leaf could not be read, so the shape cannot be judged */
  Unreadable = 'unreadable',
}

export interface RepairDecision {
  outcome: RepairOutcome;
  reason: string;
  /** the current winning revision, the one a repair is written on top of */
  winnerRev: string | null;
  /** the document to write, for the two repair outcomes only */
  body?: any;
  /** the export revision a `RestoreFromExport` came from */
  restoredFromRev?: string;
}

/** one leaf of the document, read with its revision history */
interface Leaf {
  rev: string;
  generation: number;
  body: any;
  /** this revision and every ancestor CouchDB still lists, newest first */
  history: string[];
}

const DAY_MARKER_TYPES = new Set(['DayStart', 'DayEnd']);

/** read-time keys that must never be written back */
const READ_TIME_KEYS = ['_conflicts', '_deleted_conflicts', '_revisions', '_revs_info', '_attachments', '_deleted'];

/** the generation a revision id starts with, or 0 when it has none */
function generationOf(rev: unknown): number {
  const n = typeof rev === 'string' ? Number(rev.split('-')[0]) : NaN;
  return Number.isInteger(n) ? n : 0;
}

/** every revision named in a `_revisions` block, newest first */
function historyOf(body: any): string[] {
  const revs = body?._revisions;
  if (!revs || !Number.isInteger(revs.start) || !Array.isArray(revs.ids)) return typeof body?._rev === 'string' ? [body._rev] : [];
  return revs.ids.map((hash: unknown, i: number) => `${revs.start - i}-${String(hash)}`);
}

/** the leaves of one document, split into the readable ones and a count of the rest */
function readLeaves(leafAnswers: any[]): { leaves: Leaf[]; unreadable: number } {
  const leaves: Leaf[] = [];
  let unreadable = 0;
  // for each answer row: an `ok` body is a leaf, anything else (missing, error) is not readable
  for (const answer of Array.isArray(leafAnswers) ? leafAnswers : []) {
    const body = answer?.ok;
    if (!body || typeof body !== 'object' || typeof body._rev !== 'string') {
      unreadable++;
      continue;
    }
    leaves.push({ rev: body._rev, generation: generationOf(body._rev), body, history: historyOf(body) });
  }
  return { leaves, unreadable };
}

/** the leaf CouchDB shows: the live one with the highest generation, then the highest revision id */
function couchWinner(live: Leaf[]): Leaf {
  return [...live].sort((a, b) => b.generation - a.generation || (a.rev < b.rev ? 1 : a.rev > b.rev ? -1 : 0))[0];
}

/** the time half of a `<iso>|<device>` stamp; a stamp with no `|` is all time */
function isoHalf(stamp: unknown): string | null {
  if (typeof stamp !== 'string') return null;
  const iso = stamp.split('|')[0];
  return iso.length > 0 ? iso : null;
}

/** the device half of a `<iso>|<device>` stamp, or null when there is no `|` */
function deviceHalf(stamp: unknown): string | null {
  if (typeof stamp !== 'string' || !stamp.includes('|')) return null;
  return stamp.slice(stamp.indexOf('|') + 1);
}

/** how strongly a copy speaks for the user, by the rule the fixed merge uses (#6825) */
function rankOf(entry: any): number {
  return logEntryCopyRank(entry, logEntryHoldsUserData(entry));
}

/** a copy with the read-time keys removed, ready to be merged or written */
function cleaned(body: any): any {
  const copy = { ...body };
  for (const key of READ_TIME_KEYS) delete copy[key];
  return copy;
}

/** the content of a document without its bookkeeping, for comparing two copies */
function contentOf(body: any): string {
  const keys = Object.keys(body).filter(key => !key.startsWith('_') && key !== 'db__trans').sort();
  return JSON.stringify(keys.map(key => [key, body[key]]));
}

/** does a Scheduled weigh-in or check-up still carry what only completing it writes? */
function completedButScheduled(winner: any): boolean {
  if (winner.type === 'WeighIn') {
    return (typeof winner.weight_kg === 'number' && winner.weight_kg > 0) || hasMeasuredBodyFat(winner);
  }
  // the check-up page writes completionAction together with Done, and nowhere else
  if (winner.type === 'WeeklyCheckup') return winner.completionAction != null;
  return false;
}

/**
 * How far apart a merged winner's and its tombstone's write times may be. The resolver writes
 * both in one pass over the document, so measured merges are seconds apart; a close-only run
 * leaves a winner the user saved days or months earlier.
 */
const MERGE_WRITE_WINDOW_MS = 2 * 60 * 1000;

/**
 * The write time the app's own revision ids start with (8 base-36 characters of milliseconds,
 * `mvikg83u` is 2026-10-21 20:30 UTC), or null for a hash id, which carries no time. A hash id is
 * hexadecimal, so requiring a letter past `f` keeps one from being read as a time.
 */
function writeTimeOf(rev: string): number | null {
  const id = rev.split('-')[1] ?? '';
  const head = id.slice(0, 8);
  if (!/^[0-9a-z]{8}$/.test(head) || !/[g-z]/.test(head)) return null;
  const ms = parseInt(head, 36);
  return ms > Date.UTC(2024, 0, 1) && ms < Date.UTC(2100, 0, 1) ? ms : null;
}

/** the revision a resolver tombstone closed: its parent */
function closedRevOf(tombstone: Leaf): string | null {
  return tombstone.history[1] ?? null;
}

/** do both revision ids carry their write time, and was the winner written well apart from every run tombstone? */
function writtenLongBeforeRun(winner: Leaf, runCloses: Leaf[]): boolean {
  const winnerWritten = writeTimeOf(winner.rev);
  const runWritten = runCloses.map(leaf => writeTimeOf(leaf.rev)).filter((t): t is number => t != null);
  return winnerWritten != null && runWritten.length > 0 && runWritten.every(t => Math.abs(t - winnerWritten) > MERGE_WRITE_WINDOW_MS);
}

/**
 * Did a resolver run write this winner as a merge? The checks `screen` makes, in the same order:
 * a tombstone carries the winner's exact stamp, the winner has a parent (a merge writes on top of
 * the copy CouchDB showed), and the two were not written far apart.
 */
function isResolverMerge(winner: Leaf, closes: Leaf[]): boolean {
  const runCloses = closes.filter(leaf => typeof winner.body.db__trans === 'string' && leaf.body.db__trans === winner.body.db__trans);
  return runCloses.length > 0 && winner.history[1] != null && !writtenLongBeforeRun(winner, runCloses);
}

/**
 * The shape checks shared by both entry points. For a candidate it answers the winner and
 * the revisions the merge took in: the winner's parent (what CouchDB showed) and every
 * revision the merge's run closed. Only those can be restored from: an older ancestor may
 * predate an edit the user made on purpose.
 */
type Screen = { decision: RepairDecision } | { winner: Leaf; mergeInputs: Set<string> };

function screen(leafAnswers: any[], stamp: RepairStampOptions): Screen {
  const { leaves, unreadable } = readLeaves(leafAnswers);
  const live = leaves.filter(leaf => leaf.body._deleted !== true);
  const winnerRev = live.length > 0 ? couchWinner(live).rev : null;
  if (unreadable > 0) {
    return { decision: { outcome: RepairOutcome.Unreadable, reason: `${unreadable} leaf revision(s) could not be read`, winnerRev } };
  }
  if (live.length === 0) {
    return { decision: { outcome: RepairOutcome.NoResolverClose, reason: 'no live copy', winnerRev: null } };
  }
  const closes = leaves.filter(leaf => leaf.body._deleted === true && leaf.body.db__conflictClose === true);
  if (closes.length === 0) {
    return { decision: { outcome: RepairOutcome.NoResolverClose, reason: 'no resolver tombstone', winnerRev } };
  }
  const winner = couchWinner(live);
  if (stamp.repairedMarker !== null && deviceHalf(winner.body.db__trans) === stamp.repairedMarker) {
    return { decision: { outcome: RepairOutcome.AlreadyRepaired, reason: 'the winner was written by a repair', winnerRev } };
  }
  // the run that wrote the winner stamped its tombstones with the winner's own stamp
  const runCloses = closes.filter(leaf => typeof winner.body.db__trans === 'string' && leaf.body.db__trans === winner.body.db__trans);
  if (runCloses.length === 0) {
    return { decision: { outcome: RepairOutcome.ChangedSince, reason: 'the winner does not carry a resolver tombstone\'s stamp', winnerRev } };
  }
  // another device closing the same revision adds nothing; a close of a DIFFERENT revision
  // by another run means two merges happened, and their order matters
  const runClosed = new Set(runCloses.map(closedRevOf));
  const otherRuns = closes.filter(leaf => !runCloses.includes(leaf) && !runClosed.has(closedRevOf(leaf)));
  if (otherRuns.length > 0) {
    return { decision: { outcome: RepairOutcome.ManualLook, reason: `resolver tombstones from ${otherRuns.length + 1} separate runs; the merge order matters`, winnerRev } };
  }
  // a merge always writes on top of the copy CouchDB showed, so a generation-1 winner was
  // never rewritten: the run only closed branches, and changed nothing on screen
  const parentRev = winner.history[1];
  if (!parentRev) {
    return { decision: { outcome: RepairOutcome.NotDamaged, reason: 'close only: the winner was not rewritten', winnerRev } };
  }
  // a close-only run on the device that saved the winner gives its tombstone the winner's own
  // stamp, so the stamps alone cannot tell it from a merge. when both ids carry their write
  // time, a winner written long before the run was the user's save, not the run's merge
  if (writtenLongBeforeRun(winner, runCloses)) {
    return { decision: { outcome: RepairOutcome.NotDamaged, reason: 'close only: the winner was written long before the run', winnerRev } };
  }
  if (!isLogRecordEntry(winner.body)) {
    return { decision: { outcome: RepairOutcome.NoResolverClose, reason: 'not a log entry', winnerRev } };
  }
  if (winner.body.state !== 'Scheduled') {
    // only a Scheduled winner ranks below a copy the fixed rule would have preferred
    return { decision: { outcome: RepairOutcome.NotDamaged, reason: `merged, and still ${String(winner.body.state)}`, winnerRev } };
  }
  const mergeInputs = new Set([parentRev, ...[...runClosed].filter((rev): rev is string => rev != null)]);
  return { winner, mergeInputs };
}

/**
 * Should the caller fetch this document's copies from the nightly exports before deciding?
 * Only for a resolver merge whose winner is Scheduled: every other shape is decided
 * without them, and reading the exports costs a download per database.
 */
export function wantsExportCopies(_docId: string, leafAnswers: any[], stamp: RepairStampOptions = SERVER_REPAIR_STAMP): boolean {
  return !('decision' in screen(leafAnswers, stamp));
}

/**
 * Decide what to do with one log entry.
 *
 * `leafAnswers` is every leaf of the document as `_bulk_get?revs=true` or
 * `GET <doc>?open_revs=all&revs=true` answers it. `exportCopies` is every copy of this
 * document found in the nightly exports (any night, any branch); the rule keeps only those
 * on this document's own history.
 */
export function decideRepair(docId: string, leafAnswers: any[], exportCopies: any[], stamp: RepairStampOptions = SERVER_REPAIR_STAMP): RepairDecision {
  const screened = screen(leafAnswers, stamp);
  if ('decision' in screened) return screened.decision;
  const { winner, mergeInputs } = screened;
  const winnerBody = winner.body;
  const winnerRev = winner.rev;
  const isDayMarker = DAY_MARKER_TYPES.has(winnerBody.type);

  // the export copies of the revisions the merge took in. any other copy, an older ancestor
  // or another branch, could undo an edit the user made on purpose
  const usable = (Array.isArray(exportCopies) ? exportCopies : [])
    .filter(copy => copy && copy._id === docId && typeof copy._rev === 'string' && mergeInputs.has(copy._rev))
    .filter(copy => isLogRecordEntry(copy));
  const best = usable.sort((a, b) => rankOf(b) - rankOf(a) || (getTransactionTimeMs(b) || 0) - (getTransactionTimeMs(a) || 0))[0];

  if (best && rankOf(best) > rankOf(winnerBody)) {
    // a merge keeps every field an input held, so a winner missing one is a later save by
    // somebody, not the run's merge. this is the test hash ids get, which carry no time
    const dropped = Object.keys(best).filter(key => !key.startsWith('_') && !key.startsWith('db__') && best[key] != null && winnerBody[key] == null);
    if (dropped.length > 0) {
      return { outcome: RepairOutcome.ChangedSince, reason: `the winner lacks ${dropped.join(', ')}, which input ${String(best._rev)} held; a merge keeps every field`, winnerRev };
    }
    if (isDayMarker) {
      return { outcome: RepairOutcome.ReportOnly, reason: 'a reopened day marker; the app ticks a past one again by itself (#6431)', winnerRev, restoredFromRev: best._rev };
    }
    // the fixed rule's merge with the user's copy preferred on every tie: shared fields go
    // back to the user's values, fields only the placeholder had and longer lists stay
    const merged = mergeConflictingDocsPreferring(cleaned(best), cleaned(winnerBody), false) as any;
    const body = { ...merged, _id: docId, _rev: winnerRev, db__trans: `${isoHalf(best.db__trans) ?? isoHalf(winnerBody.db__trans)}|${stamp.stampDevice}` };
    if (contentOf(body) === contentOf(winnerBody)) {
      return { outcome: RepairOutcome.NotDamaged, reason: 'the fixed merge gives the same content', winnerRev };
    }
    return { outcome: RepairOutcome.RestoreFromExport, reason: `export copy ${best._rev} is ${String(best.state)}`, winnerRev, body, restoredFromRev: best._rev };
  }

  if (completedButScheduled(winnerBody)) {
    const body = { ...cleaned(winnerBody), _id: docId, _rev: winnerRev, state: 'Done', db__trans: `${isoHalf(winnerBody.db__trans)}|${stamp.stampDevice}` };
    return { outcome: RepairOutcome.StateToDone, reason: `${String(winnerBody.type)} holds what completing it writes, but says Scheduled`, winnerRev, body };
  }

  if (winnerBody.type === 'Workout' || winnerBody.type === 'Meal') {
    const why = usable.length === 0 ? 'no export holds a copy the merge took in' : 'no copy the merge took in ranks higher';
    return { outcome: RepairOutcome.ReportOnly, reason: `a merged Scheduled ${String(winnerBody.type)}; ${why}`, winnerRev };
  }
  return { outcome: RepairOutcome.NotDamaged, reason: 'merged, and nothing shows a completion was lost', winnerRev };
}

/**
 * The content fields a repair changes, by name only, so the audit shows what a write does
 * without printing anyone's values. `_rev` and the stamp change on every write and are left out.
 */
export function changedFieldNames(before: any, after: any): string[] {
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys]
    .filter(key => !key.startsWith('_') && key !== 'db__trans')
    .filter(key => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
    .sort();
}

/** one row of the audit file, for an entry that is not plainly untouched */
export interface AuditRow {
  db: string;
  docId: string;
  outcome: RepairOutcome;
  winnerRev: string | null;
  restoredFromRev: string | null;
  /** state before, and after the repair when one is written */
  stateBefore: string | null;
  stateAfter: string | null;
  /** the names of the content fields the repair changes; empty when nothing is written */
  changedFields: string[];
  reason: string;
}

/** a markdown table row; the cells hold ids, revisions, states and field names, never health values */
export function formatAuditRow(row: AuditRow): string {
  const cell = (value: string | null) => (value ?? '').replace(/\|/g, '/');
  return `| ${cell(row.db)} | ${cell(row.docId)} | ${row.outcome} | ${cell(row.winnerRev)} | ${cell(row.restoredFromRev)} | ${cell(row.stateBefore)} | ${cell(row.stateAfter)} | ${cell(row.changedFields.join(', '))} | ${cell(row.reason)} |`;
}

// #6836 — THE CLOSE-ONLY REPAIR (`--close-only`): log-entry copies the resolver's age rule (G2)
// closed without merging while an empty placeholder stayed on screen, restored whole from the
// nightly exports. Why exports hold almost none of them, what is written and what is only
// reported, and the 2026-10-25 prod result: design-docs/sync-integrity.md, "Repairing what the
// old tie-break wrote (#6829)".
//
// The stamp is the newer of the two copies' times (the winner's), as #6834 writes it. A device
// still holding the placeholder in memory merges its next save by the newer stamp
// (database-wrapper-force-push.ts), so the copy's own older time would let that save reopen it.

type CloseOnlyScreen = { decision: RepairDecision } | { merge: true } | { winner: Leaf; closedBy: Map<string, Leaf> };

/**
 * The shape checks for `--close-only`. Answers a final decision, a hand-off to the merge rule,
 * or the winner plus each revision closed beside it and the tombstone that closed it.
 */
function screenCloseOnly(leafAnswers: any[], stamp: RepairStampOptions): CloseOnlyScreen {
  const { leaves, unreadable } = readLeaves(leafAnswers);
  const live = leaves.filter(leaf => leaf.body._deleted !== true);
  const winnerRev = live.length > 0 ? couchWinner(live).rev : null;
  if (unreadable > 0) {
    return { decision: { outcome: RepairOutcome.Unreadable, reason: `${unreadable} leaf revision(s) could not be read`, winnerRev } };
  }
  if (live.length === 0) {
    return { decision: { outcome: RepairOutcome.NoResolverClose, reason: 'no live copy', winnerRev: null } };
  }
  const winner = couchWinner(live);
  const onWinnerBranch = new Set(winner.history);
  const closes = leaves.filter(leaf => leaf.body._deleted === true && leaf.body.db__conflictClose === true);
  // each revision the resolver closed beside the winner, with the tombstone that closed it
  const closedBy = new Map<string, Leaf>();
  for (const tombstone of closes) {
    const closedRev = closedRevOf(tombstone);
    if (closedRev && !onWinnerBranch.has(closedRev)) closedBy.set(closedRev, tombstone);
  }
  if (closedBy.size === 0) {
    return { decision: { outcome: RepairOutcome.NoResolverClose, reason: 'no resolver-closed revision beside the winner', winnerRev } };
  }
  if (isResolverMerge(winner, closes)) return { merge: true };
  if (stamp.repairedMarker !== null && deviceHalf(winner.body.db__trans) === stamp.repairedMarker) {
    return { decision: { outcome: RepairOutcome.AlreadyRepaired, reason: 'the winner was written by a repair', winnerRev } };
  }
  if (!isLogRecordEntry(winner.body)) {
    return { decision: { outcome: RepairOutcome.NoResolverClose, reason: 'not a log entry', winnerRev } };
  }
  if (rankOf(winner.body) >= 2) {
    return { decision: { outcome: RepairOutcome.NotDamaged, reason: 'the shown copy holds user data', winnerRev } };
  }
  return { winner, closedBy };
}

/** Should the caller fetch this document's copies from the nightly exports before deciding? */
export function wantsCloseOnlyExportCopies(docId: string, leafAnswers: any[], stamp: RepairStampOptions = SERVER_REPAIR_STAMP): boolean {
  const screened = screenCloseOnly(leafAnswers, stamp);
  if ('merge' in screened) return wantsExportCopies(docId, leafAnswers, stamp);
  return 'winner' in screened;
}

/**
 * Decide what the close-only repair does with one log entry. Same inputs as `decideRepair`:
 * every leaf with its history, and every copy of the document found in the nightly exports.
 * On a device (#6847) the copies are the ones its own conflict archive kept instead.
 */
export function decideCloseOnlyRestore(docId: string, leafAnswers: any[], exportCopies: any[], stamp: RepairStampOptions = SERVER_REPAIR_STAMP): RepairDecision {
  const screened = screenCloseOnly(leafAnswers, stamp);
  if ('merge' in screened) return decideRepair(docId, leafAnswers, exportCopies, stamp);
  if ('decision' in screened) return screened.decision;
  const { winner, closedBy } = screened;
  const winnerRev = winner.rev;

  // only the exact revision that was closed: an older ancestor may predate an edit the user made
  const usable = (Array.isArray(exportCopies) ? exportCopies : [])
    .filter(copy => copy && copy._id === docId && typeof copy._rev === 'string' && closedBy.has(copy._rev))
    .filter(copy => isLogRecordEntry(copy));
  const best = usable.sort((a, b) => rankOf(b) - rankOf(a) || (getTransactionTimeMs(b) || 0) - (getTransactionTimeMs(a) || 0))[0];
  if (!best) {
    return { outcome: RepairOutcome.Unrecoverable, reason: 'no export holds a revision the resolver closed', winnerRev };
  }
  if (rankOf(best) <= rankOf(winner.body)) {
    return { outcome: RepairOutcome.NotDamaged, reason: `export copy ${best._rev} ranks no higher than the shown copy`, winnerRev };
  }
  // a tombstone's history is [tombstone, closed revision, its ancestors...]
  const onWinnerBranch = new Set(winner.history);
  const tombstone = closedBy.get(best._rev)!;
  const shared = tombstone.history.slice(2).find(rev => onWinnerBranch.has(rev));
  if (shared) {
    return { outcome: RepairOutcome.ReportOnly, reason: `both copies descend from ${shared}; the shown one may be a deliberate Restore`, winnerRev, restoredFromRev: best._rev };
  }
  // a winner past generation 1 was written on top of something. unless both ids carry their
  // write time and it predates the close, the user may have changed it since: a Restore after
  // an earlier --apply of this repair looks exactly like the original placeholder
  if (winner.generation > 1) {
    const winnerWritten = writeTimeOf(winner.rev);
    const closeWritten = writeTimeOf(tombstone.rev);
    if (winnerWritten == null || closeWritten == null || winnerWritten > closeWritten) {
      return { outcome: RepairOutcome.ReportOnly, reason: 'the shown copy may have been written after the close', winnerRev, restoredFromRev: best._rev };
    }
  }
  if (DAY_MARKER_TYPES.has(winner.body.type)) {
    return { outcome: RepairOutcome.ReportOnly, reason: 'a day marker; the app ticks a past one again by itself (#6431)', winnerRev, restoredFromRev: best._rev };
  }
  if (rankOf(winner.body) !== 0) {
    return { outcome: RepairOutcome.ReportOnly, reason: 'the shown copy is not an untouched placeholder', winnerRev, restoredFromRev: best._rev };
  }
  // the stamp: the newer of the two copies' times, or whichever copy has one
  const winnerMs = getTransactionTimeMs(winner.body);
  const bestMs = getTransactionTimeMs(best);
  const winnerHasStamp = Number.isFinite(winnerMs);
  const copyHasStamp = Number.isFinite(bestMs);
  let stampSource: any = null;
  if (winnerHasStamp && (!copyHasStamp || winnerMs >= bestMs)) {
    stampSource = winner.body;
  } else if (copyHasStamp) {
    stampSource = best;
  }
  if (!stampSource) {
    return { outcome: RepairOutcome.ReportOnly, reason: 'neither copy carries a stamp to write the repair with', winnerRev, restoredFromRev: best._rev };
  }
  const body = { ...cleaned(best), _id: docId, _rev: winnerRev, db__trans: `${isoHalf(stampSource.db__trans)}|${stamp.stampDevice}` };
  return { outcome: RepairOutcome.RestoreWhole, reason: `export copy ${best._rev} is ${String(best.state)}`, winnerRev, body, restoredFromRev: best._rev };
}
